import { DeleteOutlined, InfoCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Button, Checkbox, DatePicker, Input, InputNumber, Select, Tooltip } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import type { RepeatableColumn, SelectOption, WorkflowField } from '@/api/workflows';
import { BmoRadioGroup } from '@/components/BmoRadioGroup';
import { ICON_BTN_PROPS } from '@/components/iconButton';

import type { FieldValue, RepeatableRowValue } from './FieldRenderer';
import {
  computeRepeatableSum,
  readUnitMode,
  resolveSumTarget,
  resolveUnitModeTarget,
  unitModeKey,
  type UnitMode,
} from './repeatableSum';

/**
 * Runtime control for a `RepeatableRow` field. Renders one row per entry in
 * `value` using the field's `repeatableTemplate` as the column spec; appends
 * a "+ Add Another Row" button at the bottom and a per-row red trash icon
 * for removal. Layout matches the dashed-border container the BMO Carlyle
 * smartform's Step 5 mockups use.
 *
 * Designed to compose with regular conditional ShortText / Number fields
 * around it — if a particular form needs a "header row" above the repeatable
 * list (e.g. clause (w)'s "Individual who established trust" pair), the
 * originator drops two ShortTexts above the RepeatableRow and shares the
 * same visibility condition. No nested-header primitive is needed.
 */
export function RepeatableRowControl({
  field,
  value,
  onChange,
  language,
  getValueById,
  setValueById,
}: {
  field: WorkflowField;
  value: RepeatableRowValue[];
  onChange: (next: RepeatableRowValue[]) => void;
  language: 'en' | 'fr';
  /** Cross-field value lookup — supplied by FieldRenderer for RepeatableRow
   *  fields carrying a `sumValidation` that targets another field's value, and
   *  for reading a dual-unit column's current mode. */
  getValueById?: (id: string) => FieldValue;
  /** Persists a dual-unit column's chosen mode. Keyed by COLUMN, not row —
   *  the toggle governs the whole column, matching live RIF where it is drawn
   *  once in the column header. */
  setValueById?: (id: string, next: FieldValue) => void;
}) {
  const { t } = useTranslation();
  const template = field.repeatableTemplate ?? [];

  // Empty rows render as a single editable placeholder row so the advisor
  // sees something to fill in immediately — matches the mockups.
  const rows: RepeatableRowValue[] = value.length === 0 ? [emptyRow(template)] : value;

  // Column widths come from each column's authored width percentage (1–100),
  // applied as a literal `%` track so the value is taken at face value: columns
  // whose percentages sum to under 100 leave blank space on the right rather
  // than stretching to fill. A column with no percentage set falls back to an
  // equal 1fr share, so an all-unset template still renders equal columns.
  //
  // The gaps have to come OUT of those percentages. A grid of `20% 15% …`
  // summing to 100 plus `gap: 12` is 100% + (n-1)×12px wide — it overflows its
  // container and clips the last column. RIF's six-column fund table hit this:
  // five gaps put 60px past the right edge and Payment was cut in half.
  //
  // Subtracting an equal share of the total gap from every track makes the sum
  // exact again — 100% − 60px of tracks + 60px of gaps — while preserving the
  // face-value semantics above. minmax(0, …) stops a wide input from pushing
  // its track past the computed width.
  // Each dual-unit column's CURRENT mode, resolved once per render so the
  // header control, the cell inputs and the totals below all agree.
  const unitModes: Record<string, UnitMode> = {};
  for (const col of template) {
    if (col.unitToggle) unitModes[col.id] = readUnitMode(field, col, getValueById);
  }

  // The delete button sits in a 40px gutter to the right of the grid. It has
  // to be reserved on EVERY row and on the header, not just the rows that
  // happen to show a button — otherwise an anchored first row (no button) is
  // 40px wider than the rows below it and the columns visibly disagree.
  const removeGutterPx = 40;
  // True when ANY row can be removed. Both variants of the per-row rule below
  // reduce to the same condition — anchored makes rows 1+ removable, unanchored
  // makes any row removable while one survives — so a single row never has a
  // gutter and two or more always do.
  const showRemoveGutter = rows.length > 1;

  const gapPx = 12;
  const gapShare = template.length > 1 ? (gapPx * (template.length - 1)) / template.length : 0;
  const gridTemplateColumns = template
    .map((c) =>
      c.widthPercent && c.widthPercent > 0
        ? `minmax(0, calc(${c.widthPercent}% - ${gapShare}px))`
        : 'minmax(0, 1fr)',
    )
    .join(' ');

  // Card layout (displayAs 'card', the 2026-07-24 BMO "Funds" design): each
  // entry becomes a bordered block with its labels inside it and the columns
  // WRAPPING into a two-column grid, instead of one wide table line under a
  // shared header. Presentation only — template, row cap and the cross-row
  // sum rule are untouched, which is why the design could stay on
  // RepeatableRow rather than moving to a repeatable section (which has no
  // cross-instance sum).
  const cardLayout = field.displayAs === 'card';
  // In card layout `widthPercent` picks how many of the two tracks a column
  // takes: >= 100 spans the full width (Fund name), anything else takes one
  // (the 50/50 pairs). Unset behaves as a half so an unconfigured template
  // still lays out sensibly rather than collapsing to one column.
  const cardSpan = (c: RepeatableColumn) => ((c.widthPercent ?? 50) >= 100 ? 2 : 1);

  // A shared header row surfaces each column's name + help bubble once above the
  // data rows. Card layout labels each cell individually instead.
  const showHeader = !cardLayout && template.some((c) => columnLabel(c, language).length > 0);

  const updateCell = (rowIdx: number, colId: string, next: string | number | boolean | null) => {
    const draft = rows.map((r, i) => (i === rowIdx ? { ...r, [colId]: next } : r));
    onChange(draft);
  };

  const addRow = () => onChange([...rows, emptyRow(template)]);

  const removeRow = (idx: number) => {
    const next = rows.filter((_, i) => i !== idx);
    // If the advisor removes the last row, fall back to a single empty
    // placeholder so the dashed container always has something visible.
    onChange(next.length === 0 ? [] : next);
  };

  // Hide the "+ Add Another Row" button once the originator-authored cap
  // is reached. Null = unlimited.
  const maxRows = field.maxRows ?? null;
  const canAddRow = maxRows === null || rows.length < maxRows;
  // Button copy names the thing being added and states the cap, per the
  // 2026-07-24 BMO design ("ADD ANOTHER FUND (MAXIMUM 5)"). The noun comes
  // from the field's own label so the originator controls it; falls back to
  // the generic "Add Another Row" when the field is unlabelled — or when the
  // originator hid the label, since naming a noun the advisor cannot see
  // anywhere on the page reads as a stray word.
  const addRowNoun = field.hideLabel
    ? undefined
    : (language === 'fr' && field.labelFr ? field.labelFr : field.labelEn)?.trim();
  const addRowLabel = addRowNoun
    ? maxRows !== null
      ? t('formRenderer.repeatable.addRowNamedCapped', { name: addRowNoun, max: maxRows })
      : t('formRenderer.repeatable.addRowNamed', { name: addRowNoun })
    : t('formRenderer.repeatable.addRow');

  // Cross-row sum summary — only rendered when the field carries a
  // sumValidation AND the target is currently resolvable. Rendered live
  // so the advisor sees the delta while editing.
  const sv = field.sumValidation ?? null;
  const currentSum = sv ? computeRepeatableSum(field, rows) : null;
  const targetSum = sv ? resolveSumTarget(sv, getValueById) : null;
  // Originator-authored caption for the total, shown in place of the generic
  // "Current sum" wording. Prefer the active language, fall back to the other
  // so a single-language caption still shows rather than reverting to generic.
  const sumLabel = sv
    ? (language === 'fr' ? sv.labelFr || sv.labelEn : sv.labelEn || sv.labelFr) || null
    : null;
  const showSummary = sv !== null && currentSum !== null;
  const summaryOff = showSummary && targetSum !== null && Math.abs(currentSum - targetSum) >= 0.005;

  // Per-mode running totals for dual-unit columns. Only shown when the ACTIVE
  // mode carries a target — RIF leaves dollar mode unconstrained, and showing
  // "Total: $0 / target —" there would be noise.
  const unitTotals = template.flatMap((col) => {
    const toggle = col.unitToggle;
    if (!toggle) return [];
    // unitModes is populated for every column carrying a toggle, but read it
    // defensively so a template change can't produce an undefined mode.
    const mode = unitModes[col.id] ?? readUnitMode(field, col, getValueById);
    const target = resolveUnitModeTarget(toggle, mode, getValueById);
    if (target === null) return [];
    const total = computeRepeatableSum(field, rows, col.id) ?? 0;
    return [
      {
        id: col.id,
        label: columnLabel(col, language),
        mode,
        total,
        target,
        off: Math.abs(total - target) >= 0.005,
      },
    ];
  });

  return (
    <div
      style={{
        // Cards carry their own borders, so the outer dashed/tinted frame
        // (which reads as a table container) is dropped in that mode.
        border: cardLayout ? undefined : '1px dashed #93c5fd',
        borderRadius: cardLayout ? undefined : 8,
        padding: cardLayout ? 0 : 16,
        background: cardLayout ? undefined : '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {showHeader && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns,
            gap: gapPx,
            paddingRight: showRemoveGutter ? removeGutterPx : 0,
          }}
        >
          {template.map((col) => {
            const label = columnLabel(col, language);
            const help = columnHelp(col, language);
            const toggle = col.unitToggle ?? null;
            const mode = unitModes[col.id];
            return (
              <div
                key={col.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#334155',
                }}
              >
                <span>
                  {label}
                  {col.required ? ' *' : ''}
                </span>
                {/* Dual-unit control: ONE checkbox per column, in the header,
                    flipping every row at once. Live RIF renders it exactly
                    here, labelled "$/%". */}
                {toggle && (
                  <Checkbox
                    checked={mode === 'percent'}
                    disabled={!setValueById}
                    onChange={(e) =>
                      setValueById?.(
                        unitModeKey(field.id, col.id),
                        e.target.checked ? 'percent' : 'dollar',
                      )
                    }
                    aria-label={t('formRenderer.repeatable.unitToggleAria', {
                      column: label,
                      defaultValue: `Switch ${label} between dollars and percent`,
                    })}
                    style={{ marginLeft: 4, fontWeight: 400 }}
                  >
                    <span style={{ fontSize: 12, color: '#475569' }}>
                      {(language === 'fr' ? toggle.labelFr : toggle.labelEn) || '$/%'}
                    </span>
                  </Checkbox>
                )}
                {help && (
                  <Tooltip title={help}>
                    <InfoCircleOutlined style={{ color: '#0179C1', fontSize: 12 }} />
                  </Tooltip>
                )}
              </div>
            );
          })}
        </div>
      )}
      {rows.map((row, rowIdx) => {
        // Anchoring is now an AUTHORED property, not a side effect of layout.
        // It had been implied by card layout alone, which left "table chrome
        // with a locked first row" — live RIF's fund table — unexpressible.
        //
        // Unset falls back to the historical default so nothing already built
        // changes: cards anchor row 0, tables allow removing any row while one
        // survives.
        const anchored = field.anchorFirstRow ?? cardLayout;
        const removable = anchored ? rowIdx > 0 : rows.length > 1;
        const removeButton = (
          <Button
            {...ICON_BTN_PROPS}
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeRow(rowIdx)}
            aria-label={t('formRenderer.repeatable.removeRow')}
            style={
              cardLayout
                ? ICON_BTN_PROPS.style
                : { ...ICON_BTN_PROPS.style, position: 'absolute', top: 4, right: 0 }
            }
          />
        );
        const cells = template.map((col) => {
          const cellLabel = columnLabel(col, language);
          const cellHelp = columnHelp(col, language);
          return (
            <div
              key={col.id}
              style={cardLayout ? { gridColumn: `span ${cardSpan(col)}` } : undefined}
            >
              {cardLayout && cellLabel.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 13,
                    color: '#334155',
                    marginBottom: 6,
                  }}
                >
                  <span>
                    {cellLabel}
                    {col.required ? ' *' : ''}
                  </span>
                  {cellHelp && (
                    <Tooltip title={cellHelp}>
                      <InfoCircleOutlined style={{ color: '#0179C1', fontSize: 12 }} />
                    </Tooltip>
                  )}
                </div>
              )}
              <RepeatableCellInput
                unitMode={unitModes[col.id]}
                col={col}
                value={row[col.id] ?? null}
                onChange={(next) => updateCell(rowIdx, col.id, next)}
                language={language}
                // The card labels every cell, so a placeholder repeating the
                // label would just be noise under it.
                hidePlaceholder={cardLayout}
              />
            </div>
          );
        });

        // Card: the remove button sits on the same band as the inputs, to the
        // right of the last cell. `alignItems: flex-end` lines it up with the
        // input rather than the cell label above it, and survives a label
        // wrapping to two lines. It previously had its own strip above the
        // grid, which left an empty band across the top of every card.
        // Absolutely positioning it (as the table does) is not an option —
        // that drops it on top of the first full-width input.
        if (cardLayout) {
          return (
            <div
              key={rowIdx}
              className="bmo-repeatable-card"
              style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}
            >
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 16,
                }}
              >
                {cells}
              </div>
              {removable && <div style={{ paddingBottom: 4 }}>{removeButton}</div>}
            </div>
          );
        }

        return (
          <div
            key={rowIdx}
            style={{
              position: 'relative',
              display: 'grid',
              gridTemplateColumns,
              gap: gapPx,
              // Reserved on every row, so an un-removable row lines up with a
              // removable one.
              paddingRight: showRemoveGutter ? removeGutterPx : 0,
            }}
          >
            {cells}
            {removable && removeButton}
          </div>
        );
      })}
      {canAddRow && (
        <Button
          type="link"
          icon={<PlusCircleOutlined style={{ fontSize: 18 }} />}
          onClick={addRow}
          // Same BMO treatment as the repeatable-SECTION add button (see
          // FormRenderer) so the two "add another" affordances read alike.
          style={{
            alignSelf: 'flex-start',
            paddingLeft: 0,
            height: 'auto',
            color: '#0079C1',
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
          }}
        >
          {addRowLabel}
        </Button>
      )}
      {unitTotals.map((u) => (
        <div
          key={`total-${u.id}`}
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
            fontSize: 13,
            fontWeight: 500,
            color: u.off ? '#b91c1c' : '#059669',
            paddingTop: 4,
            borderTop: '1px solid #e5e7eb',
          }}
          role={u.off ? 'alert' : undefined}
        >
          <span>
            {t('formRenderer.repeatable.sumCurrentLabeled', {
              label: u.label,
              value: u.mode === 'percent' ? `${formatSumValue(u.total)}%` : formatSumValue(u.total),
            })}
          </span>
          <span>
            {t('formRenderer.repeatable.sumTarget', {
              value:
                u.mode === 'percent' ? `${formatSumValue(u.target)}%` : formatSumValue(u.target),
            })}
          </span>
        </div>
      ))}
      {showSummary && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
            fontSize: 13,
            fontWeight: 500,
            color: summaryOff ? '#b91c1c' : '#059669',
            paddingTop: 4,
            borderTop: '1px solid #e5e7eb',
          }}
          role={summaryOff ? 'alert' : undefined}
        >
          <span>
            {sumLabel
              ? t('formRenderer.repeatable.sumCurrentLabeled', {
                  label: sumLabel,
                  value: formatSumValue(currentSum!),
                })
              : t('formRenderer.repeatable.sumCurrent', {
                  value: formatSumValue(currentSum!),
                })}
          </span>
          {targetSum !== null && (
            <span>
              {t('formRenderer.repeatable.sumTarget', {
                value: formatSumValue(targetSum),
              })}
            </span>
          )}
          {summaryOff && targetSum !== null && (
            <span>
              {t('formRenderer.repeatable.sumDelta', {
                value: formatSumValue(currentSum! - targetSum),
              })}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function formatSumValue(v: number): string {
  return new Intl.NumberFormat('en-CA', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(v);
}

function RepeatableCellInput({
  col,
  value,
  onChange,
  language,
  hidePlaceholder,
  unitMode,
}: {
  col: RepeatableColumn;
  value: string | number | boolean | null;
  onChange: (next: string | number | boolean | null) => void;
  language: 'en' | 'fr';
  /** Card layout renders a real label above each cell, so the label-derived
   *  placeholder underneath it would be redundant. An originator-authored
   *  placeholder is still honoured. */
  hidePlaceholder?: boolean;
  /** Set on a dual-unit column — drives the affix and the 0–100 bound. */
  unitMode?: UnitMode;
}) {
  const label = (language === 'fr' ? col.labelFr : col.labelEn) || col.labelEn || col.labelFr;
  // A dual-unit column's label carries its unit, the way live RIF shows
  // "Amount $" / "Amount %". The authored label stays unit-free so the
  // originator writes it once.
  const unitSuffix = unitMode ? (unitMode === 'percent' ? ' %' : ' $') : '';
  const labelWithRequired = `${label}${unitSuffix}${col.required ? ' *' : ''}`;
  const placeholder = col.placeholder ?? (hidePlaceholder ? undefined : labelWithRequired);

  if (col.type === 'Number') {
    const percent = unitMode === 'percent';
    return (
      <InputNumber
        controls={false}
        value={typeof value === 'number' ? value : null}
        onChange={(n) => onChange(n ?? null)}
        placeholder={placeholder}
        // Percent mode is bounded 0–100; dollars are not. Only applied on a
        // dual-unit column so ordinary Number columns keep their own rules.
        min={percent ? 0 : undefined}
        max={percent ? 100 : undefined}
        prefix={unitMode === 'dollar' ? '$' : undefined}
        suffix={percent ? '%' : undefined}
        style={{ width: '100%' }}
      />
    );
  }
  if (col.type === 'Date') {
    const d = typeof value === 'string' && value.length > 0 ? dayjs(value) : null;
    return (
      <DatePicker
        value={d && d.isValid() ? d : null}
        onChange={(picked: Dayjs | null) =>
          onChange(picked && picked.isValid() ? picked.format('YYYY-MM-DD') : null)
        }
        placeholder={placeholder}
        format="DD/MM/YYYY"
        style={{ width: '100%' }}
      />
    );
  }
  if (col.type === 'SingleSelect') {
    return (
      <Select
        value={typeof value === 'string' ? value : undefined}
        onChange={(v) => onChange(v ?? null)}
        placeholder={placeholder}
        style={{ width: '100%' }}
        options={col.options.map((o) => ({
          value: o.value,
          label: optionLabel(o, language),
        }))}
        allowClear
      />
    );
  }
  if (col.type === 'RadioGroup') {
    return (
      <BmoRadioGroup
        value={typeof value === 'string' ? value : undefined}
        onChange={(v) => onChange(v ?? null)}
        options={col.options.map((o) => ({
          value: o.value,
          label: optionLabel(o, language),
        }))}
      />
    );
  }
  // ShortText / LongText fall through. LongText uses TextArea; ShortText is a
  // regular Input. No mask/validate yet — out of scope for v1.
  if (col.type === 'LongText') {
    return (
      <Input.TextArea
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(applyInputRestriction(col.inputRestriction, e.target.value))}
        placeholder={placeholder}
        maxLength={col.maxLength ?? undefined}
        autoSize={{ minRows: 2, maxRows: 6 }}
      />
    );
  }
  return (
    <Input
      value={typeof value === 'string' ? value : ''}
      onChange={(e) => onChange(applyInputRestriction(col.inputRestriction, e.target.value))}
      placeholder={placeholder}
      maxLength={col.maxLength ?? undefined}
    />
  );
}

/** Per-column input-restriction. Same logic as the scalar ShortText/LongText
 *  renderer in FieldRenderer.tsx — kept inline so the RepeatableRowControl
 *  isn't pulled into FieldRenderer's circular import path. */
function applyInputRestriction(restriction: string | null | undefined, value: string): string {
  if (!restriction) return value;
  if (restriction === 'digits') return value.replace(/[^0-9]/g, '');
  if (restriction === 'letters') {
    const kept = value.replace(/[^\p{L}\p{M}'’\- ]/gu, '');
    return kept.replace(/(^|[^\p{L}\p{M}])(\p{M}+)/gu, '$1');
  }
  if (restriction === 'alphanumeric') return value.replace(/[^\p{L}\p{N} ]/gu, '');
  return value;
}

function optionLabel(o: SelectOption, language: 'en' | 'fr'): string {
  return (language === 'fr' ? o.labelFr : o.labelEn) || o.labelEn || o.labelFr;
}

/** Language-aware column header label; falls back to the other language when the
 *  active one is blank so a single-language column still shows a name. */
function columnLabel(col: RepeatableColumn, language: 'en' | 'fr'): string {
  return (language === 'fr' ? col.labelFr : col.labelEn) || col.labelEn || col.labelFr || '';
}

/** Language-aware column help description, or '' when the column has no help
 *  authored in either language. */
function columnHelp(col: RepeatableColumn, language: 'en' | 'fr'): string {
  const active = language === 'fr' ? col.helpFr : col.helpEn;
  const fallback = language === 'fr' ? col.helpEn : col.helpFr;
  return (active?.trim() || fallback?.trim() || '') as string;
}

function emptyRow(template: RepeatableColumn[]): RepeatableRowValue {
  const row: RepeatableRowValue = {};
  for (const col of template) row[col.id] = null;
  return row;
}
